import React, { useState, useEffect } from "react";
import { getAllPublications, type Publication } from "@/lib/markdown";

const Publications: React.FC = () => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    const loadPublications = async () => {
      try {
        const pubs = await getAllPublications();
        setPublications(pubs);
      } catch (error) {
        console.error("Error loading publications:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPublications();
  }, []);

  const copyBibtex = async (bibtex: string, slug: string) => {
    try {
      await navigator.clipboard.writeText(bibtex);
      setCopiedId(slug);
      setTimeout(() => setCopiedId(null), 2000); // Reset after 2 seconds
    } catch (error) {
      console.error("Failed to copy bibtex:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = bibtex;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedId(slug);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 font-mono">
          Publications
        </h1>
        <div className="font-mono text-sm">Loading publications...</div>
      </div>
    );
  }

  const groupedByYear = publications.reduce(
    (acc, pub) => {
      if (!acc[pub.year]) acc[pub.year] = [];
      acc[pub.year].push(pub);
      return acc;
    },
    {} as Record<number, Publication[]>
  );

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 font-mono">
        Publications
      </h1>

      <div className="mb-6 sm:mb-8 font-mono text-sm">
        <p>
          {publications.length}+ research papers published in top-tier
          conferences and journals. Focus areas: machine learning, video coding,
          computer vision, multimedia networking, video streaming.
        </p>
        <p className="mt-3">
          Citation metrics and charts on{" "}
          <a
            href="https://schovis.com/ekrem"
            className="link-minimal"
            target="_blank"
            rel="noopener noreferrer"
          >
            schovis.com/ekrem ↗
          </a>
        </p>
      </div>

      <div className="space-y-6 sm:space-y-8">
        {Object.entries(groupedByYear)
          .sort(([a], [b]) => Number(b) - Number(a))
          .map(([year, pubs]) => (
            <div key={year}>
              <h2 className="text-lg sm:text-xl font-bold font-mono mb-4 border-b border-black pb-1">
                {year}
              </h2>
              <div className="space-y-4 sm:space-y-6">
                {pubs.map((pub, index) => (
                  <div key={index} className="border-l-2 border-black pl-4">
                    <h3 className="font-bold font-mono text-sm mb-2">
                      {pub.title}
                    </h3>

                    {pub.authors && (
                      <div className="text-xs font-mono text-gray-600 mb-1">
                        {pub.authors.replace(/\*\*/g, "")}
                      </div>
                    )}

                    {pub.venue && (
                      <div className="text-xs font-mono text-gray-500 italic mb-2">
                        {pub.venue.replace(/\*\*/g, "")}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 mb-2">
                      {pub.paperLink && (
                        <button className="text-xs font-mono button-minimal py-1 px-2 inline-block">
                          <a
                            href={pub.paperLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Paper
                          </a>
                        </button>
                      )}

                      {pub.bibtex && (
                        <button
                          onClick={() => copyBibtex(pub.bibtex, pub.slug)}
                          className="text-xs font-mono button-minimal py-1 px-2 inline-block"
                        >
                          {copiedId === pub.slug ? "Copied!" : "Copy BibTeX"}
                        </button>
                      )}
                    </div>

                    {pub.abstract && (
                      <details className="mt-2">
                        <summary className="text-xs font-mono cursor-pointer hover:bg-black hover:text-white px-1 py-1 transition-colors duration-200 inline-block">
                          Abstract
                        </summary>
                        <div className="text-xs font-mono text-gray-700 mt-2 pl-2 border-l border-gray-300">
                          {pub.abstract.replace(/\*\*/g, "")}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>

      <div className="mt-8 sm:mt-12 border-t border-black pt-6 sm:pt-8">
        <div className="font-mono text-sm space-y-2">
          <div>
            Full publication list available on{" "}
            <a
              href="https://scholar.google.com"
              className="link-minimal"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Scholar
            </a>
          </div>
          <div>
            Preprints and code available on{" "}
            <a
              href="https://github.com/ekrcet"
              className="link-minimal"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Publications;
